import React, { createContext, useContext, useState, useEffect } from 'react';
import { CURRENCIES } from '../utils/currency';

const CurrencyContext = createContext();

// Country to currency mapping
const COUNTRY_CURRENCY_MAP = {
  // EUR countries
  'AT': 'EUR', 'BE': 'EUR', 'CY': 'EUR', 'EE': 'EUR', 'FI': 'EUR', 'FR': 'EUR',
  'DE': 'EUR', 'GR': 'EUR', 'IE': 'EUR', 'IT': 'EUR', 'LV': 'EUR', 'LT': 'EUR',
  'LU': 'EUR', 'MT': 'EUR', 'NL': 'EUR', 'PT': 'EUR', 'SK': 'EUR', 'SI': 'EUR',
  'ES': 'EUR',
  // USD countries
  'US': 'USD', 'AS': 'USD', 'GU': 'USD', 'MP': 'USD', 'PR': 'USD', 'VI': 'USD',
  'EC': 'USD', 'SV': 'USD', 'PA': 'USD',
  // SEK country
  'SE': 'SEK',
};

const STORAGE_KEY = 'gorito_currency';

export const CurrencyProvider = ({ children }) => {
  const [currency, setCurrency] = useState('SEK');
  const [detectedCountry, setDetectedCountry] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // Detect user's location and set appropriate currency
  useEffect(() => {
    const detectCurrency = async () => {
      // Check if user has manually selected a currency before
      const savedCurrency = localStorage.getItem(STORAGE_KEY);
      if (savedCurrency && CURRENCIES[savedCurrency]) {
        setCurrency(savedCurrency);
        setIsLoading(false);
        return;
      }

      // Try to detect location
      try {
        const response = await fetch('https://ipapi.co/json/', {
          signal: AbortSignal.timeout(3000) // 3 second timeout
        });
        
        if (response.ok) {
          const data = await response.json();
          const countryCode = data.country_code;
          setDetectedCountry(countryCode);
          
          // Map country to currency
          const detectedCurrency = COUNTRY_CURRENCY_MAP[countryCode] || 'SEK';
          setCurrency(detectedCurrency);
          localStorage.setItem(STORAGE_KEY, detectedCurrency);
        }
      } catch (error) {
        // Silent fail - default to SEK
        console.log('Currency detection failed, defaulting to SEK');
      } finally {
        setIsLoading(false);
      }
    };

    detectCurrency();
  }, []);

  // Manual currency change
  const changeCurrency = (newCurrency) => {
    if (CURRENCIES[newCurrency]) {
      setCurrency(newCurrency);
      localStorage.setItem(STORAGE_KEY, newCurrency);
    }
  };

  const value = {
    currency,
    changeCurrency,
    detectedCountry,
    isLoading,
    currencies: Object.values(CURRENCIES),
    currentCurrency: CURRENCIES[currency],
  };

  return (
    <CurrencyContext.Provider value={value}>
      {children}
    </CurrencyContext.Provider>
  );
};

export const useCurrency = () => {
  const context = useContext(CurrencyContext);
  if (!context) {
    throw new Error('useCurrency must be used within a CurrencyProvider');
  }
  return context;
};

export default CurrencyContext;
